// ─── Feuille : CRÉATION / PARAMÈTRES d'une grille d'EDT ───────────
// Nom, période (début/fin), plage horaire (heures entières) et durée de
// cellule. À l'enregistrement des paramètres, les cases existantes sont
// REMAPPÉES sur les nouveaux créneaux (migrerCasesConfig — le contenu suit
// les horaires). Porté FT#83.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, SelecteurDate, SelecteurHeure, Bascule, ChoixPuces, Stepper, ChampBouton, stylesForm, useUI, useT } from '@mop/core';
import { PLAGE_DEFAUT, DUREES_EDT, libelleDuree } from './services/edt';
import { emojiVacances } from './services/vacances';

const fmt = (dk) => { if (!/^\d{4}-\d{2}-\d{2}$/.test(dk || '')) return null; const [y, m, d] = dk.split('-'); return `${d}/${m}/${y}`; };
const hVersHHMM = (v) => { const h = Math.floor(v), m = Math.round((v - h) * 60); return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`; };
const hhmmVersH = (s0) => { const [h, m] = String(s0 || '').split(':').map(Number); return (h || 0) + (m || 0) / 60; };

export default function FeuilleGrilleEDT({ visible, initial, vacances = [], onEnregistrer, onFermer }) {
  // initial = null (création) | grille existante (paramètres)
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [nom, setNom] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [heureDebut, setHeureDebut] = useState(PLAGE_DEFAUT.heureDebut);
  const [heureFin, setHeureFin] = useState(PLAGE_DEFAUT.heureFin);
  const [dureeMin, setDureeMin] = useState(PLAGE_DEFAUT.dureeMin);
  const [nbSemaines, setNbSemaines] = useState(1);      // périodicité (rotation S1/S2/…)
  const [vacApplique, setVacApplique] = useState(true); // appliquer pendant les vacances (global)
  const [vacChoix, setVacChoix] = useState({});         // choix PAR période { [debut]: bool }
  const [picker, setPicker] = useState(null);   // 'debut' | 'fin'

  useEffect(() => {
    if (!visible) return;
    setNom(initial?.nom || ''); setDebut(initial?.debut || ''); setFin(initial?.fin || '');
    setHeureDebut(initial?.heureDebut ?? PLAGE_DEFAUT.heureDebut);
    setHeureFin(initial?.heureFin ?? PLAGE_DEFAUT.heureFin);
    setDureeMin(initial?.dureeMin ?? PLAGE_DEFAUT.dureeMin);
    setNbSemaines(Math.max(1, Number(initial?.nbSemaines) || 1));
    setVacApplique(initial?.vacApplique !== false);
    setVacChoix(initial?.vacChoix || {});
    setPicker(null);
  }, [visible, initial]);

  // Vacances chevauchant la période de la grille (choix par période).
  const vacancesPeriode = vacances.filter(p => p.debut && p.fin
    && (!fin || p.debut <= fin) && (!debut || p.fin >= debut));

  const valide = !!debut && !!fin && debut <= fin && heureFin > heureDebut;
  const modifie = valide && (!initial || nom !== (initial.nom || '') || debut !== (initial.debut || '')
    || fin !== (initial.fin || '') || heureDebut !== (initial.heureDebut ?? PLAGE_DEFAUT.heureDebut)
    || heureFin !== (initial.heureFin ?? PLAGE_DEFAUT.heureFin) || dureeMin !== (initial.dureeMin ?? PLAGE_DEFAUT.dureeMin)
    || nbSemaines !== Math.max(1, Number(initial?.nbSemaines) || 1)
    || vacApplique !== (initial?.vacApplique !== false)
    || JSON.stringify(vacChoix) !== JSON.stringify(initial?.vacChoix || {}));

  return (
    <>
      <Feuille visible={visible} titre={initial ? t('edt.parametres') : t('edt.nouvelleGrille')} emoji="🕐"
        hauteur={0.82} onFermer={onFermer}
        action={{ icone: initial ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !modifie,
          onPress: () => onEnregistrer({ nom: nom.trim() || t('edt.grille'), debut, fin, heureDebut, heureFin, dureeMin, nbSemaines, vacApplique, vacChoix }) }}>

        <Text style={s.lbl}>{t('edt.nomGrille')}</Text>
        <TextInput style={s.input} value={nom} onChangeText={setNom}
          placeholder={t('edt.phNomGrille')} placeholderTextColor={UI.faint} />

        <View style={{ flexDirection: 'row', gap: 10 }}>
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('calendrier.dateDebut')}</Text>
            <ChampBouton icone="📅" valeur={fmt(debut)} onPress={() => setPicker('debut')} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('finances.dateFin')}</Text>
            <ChampBouton icone="📅" valeur={fmt(fin)} onPress={() => setPicker('fin')} />
          </View>
        </View>

        {/* Plage horaire : sélecteur d'heure, comme un événement. */}
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('calendrier.heureDebut')}</Text>
            <ChampBouton icone="🕒" valeur={hVersHHMM(heureDebut)} onPress={() => setPicker('hDebut')} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('calendrier.heureFin')}</Text>
            <ChampBouton icone="🕒" valeur={hVersHHMM(heureFin)} onPress={() => setPicker('hFin')} />
          </View>
        </View>

        <Text style={s.lbl}>{t('edt.dureeCellule')}</Text>
        <ChoixPuces options={DUREES_EDT.map(d => ({ id: d, label: libelleDuree(d) }))}
          value={dureeMin} onChange={setDureeMin} />
        {/* Périodicité multi-semaines : la grille tourne sur N semaines,
            chacune configurée dans son ONGLET (S1/S2/…). */}
        <Text style={s.lbl}>{t('edt.periodicite')}</Text>
        <Stepper valeur={nbSemaines}
          onMoins={() => setNbSemaines(v => Math.max(1, v - 1))}
          onPlus={() => setNbSemaines(v => Math.min(8, v + 1))}
          apres={<Text style={s.aideInline}>{t('edt.periodiciteAide')}</Text>} />

        {/* Vacances : application GLOBALE + réglage par période ci-dessous. */}
        <View style={s.switchRow}>
          <View style={{ flex: 1 }}>
            <Text style={s.switchTitre}>{t('edt.vacApplique')}</Text>
            <Text style={s.switchSous}>{t('edt.vacAide')}</Text>
          </View>
          <Bascule valeur={vacApplique} onChange={setVacApplique} accessibilityLabel={t('edt.vacApplique')} />
        </View>
        {vacancesPeriode.length > 0 && <Text style={s.lbl}>{t('edt.vacListe')}</Text>}
        {vacancesPeriode.map(p => (
          <View key={p.debut} style={s.vacRow}>
            <Text style={{ fontSize: 15 }}>{emojiVacances(p.nom)}</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.vacNom} numberOfLines={1}>{p.nom}</Text>
              <Text style={s.vacDates}>{fmt(p.debut)} → {fmt(p.fin)}</Text>
            </View>
            <Bascule valeur={vacChoix[p.debut] ?? vacApplique} accessibilityLabel={p.nom}
              onChange={(v) => setVacChoix(x => ({ ...x, [p.debut]: v }))} />
          </View>
        ))}

        {initial && <Text style={s.aide}>{t('edt.remapAide')}</Text>}
      </Feuille>

      {/* Sélecteurs date / heure (modales sœurs — pas d'imbrication native). */}
      <SelecteurDate visible={picker === 'debut' || picker === 'fin'}
        titre={picker === 'fin' ? t('finances.dateFin') : t('calendrier.dateDebut')}
        valeur={(picker === 'fin' ? fin : debut) || debut || undefined}
        onChange={(dk) => { if (picker === 'fin') setFin(dk); else setDebut(dk); }}
        onFermer={() => setPicker(null)} />
      <SelecteurHeure visible={picker === 'hDebut' || picker === 'hFin'}
        titre={picker === 'hFin' ? t('calendrier.heureFin') : t('calendrier.heureDebut')}
        valeur={hVersHHMM(picker === 'hFin' ? heureFin : heureDebut)}
        onChange={(hhmm) => { const v = hhmmVersH(hhmm); if (picker === 'hFin') setHeureFin(v); else setHeureDebut(v); }}
        onFermer={() => setPicker(null)} />
    </>
  );
}

// Styles de formulaire : base partagée du Core + styles propres à la feuille.
const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  aide: { fontSize: 12, color: UI.muted, marginTop: 10 },
  aideInline: { flex: 1, fontSize: 11.5, color: UI.faint, marginLeft: 8 },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 14 },
  switchTitre: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  switchSous: { fontSize: 11.5, color: UI.faint, marginTop: 1 },
  vacRow: { flexDirection: 'row', alignItems: 'center', gap: 9, backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.border, borderRadius: 11, paddingHorizontal: 11, paddingVertical: 8, marginBottom: 6 },
  vacNom: { fontSize: 13, fontWeight: '700', color: UI.ink },
  vacDates: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
});
